import React, { useState, useEffect, useRef } from 'react';

/**
 * QuestionDrawing Component
 * Used for: Drawing Tests (HTP, BAUM, Wartegg, DAP)
 * Format: Image upload with canvas drawing option
 */
const QuestionDrawing = ({
    question,
    answer,
    onAnswer,
    disabled = false
}) => {
    const [uploadedImage, setUploadedImage] = useState(answer?.imageUrl || null);
    const [uploadMethod, setUploadMethod] = useState(answer?.method || 'file'); // 'file' or 'canvas'
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasDrawn, setHasDrawn] = useState(false);

    useEffect(() => {
        if (answer) {
            setUploadedImage(answer.imageUrl || null);
            setUploadMethod(answer.method || 'file');
        }
    }, [answer]);

    // Canvas drawing handlers
    const startDrawing = (e) => {
        if (disabled) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();

        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing || disabled) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();

        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
        ctx.stroke();
        setHasDrawn(true);
    };

    const stopDrawing = () => {
        setIsDrawing(false);

        if (hasDrawn) {
            // Convert canvas to data URL
            const canvas = canvasRef.current;
            const dataUrl = canvas.toDataURL('image/png');

            const answerData = {
                imageUrl: dataUrl,
                method: 'canvas',
                timestamp: new Date().toISOString()
            };

            setUploadedImage(dataUrl);
            onAnswer(question.id, answerData);
        }
    };

    const clearCanvas = () => {
        if (disabled) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        setUploadedImage(null);
        setHasDrawn(false);
        onAnswer(question.id, null);
    };

    // File upload handler
    const handleFileUpload = (e) => {
        if (disabled) return;

        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Hanya file gambar yang diperbolehkan');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('Ukuran file maksimal 5MB');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const imageUrl = event.target.result;

            const answerData = {
                imageUrl: imageUrl,
                method: 'file',
                fileName: file.name,
                fileSize: file.size,
                timestamp: new Date().toISOString()
            };

            setUploadedImage(imageUrl);
            setUploadMethod('file');
            onAnswer(question.id, answerData);
        };

        reader.readAsDataURL(file);
    };

    const removeImage = () => {
        if (disabled) return;

        setUploadedImage(null);
        onAnswer(question.id, null);
    };

    // Initialize canvas
    useEffect(() => {
        if (canvasRef.current && uploadMethod === 'canvas') {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');

            // Set canvas styling
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.strokeStyle = '#000000';
        }
    }, [uploadMethod]);

    return (
        <div className="question-drawing">
            {/* Question Text */}
            <div className="question-text mb-6">
                <div
                    className="text-lg font-semibold"
                    dangerouslySetInnerHTML={{ __html: question.soal }}
                />
            </div>

            {/* Reference Image (if provided) */}
            {question.image_path && (
                <div className="reference-image mb-6">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                        Gambar Referensi:
                    </div>
                    <img
                        src={`/storage/${question.image_path}`}
                        alt="Drawing reference"
                        className="max-w-md h-auto rounded-lg shadow-md border-2 border-gray-300"
                        loading="lazy"
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                </div>
            )}

            {/* Instructions */}
            <div className="instructions mb-6 p-4 bg-indigo-50 border-l-4 border-indigo-400 rounded">
                <p className="text-sm text-indigo-800 mb-2">
                    <strong>Instruksi:</strong>
                </p>
                <ul className="text-sm text-indigo-800 list-disc list-inside space-y-1">
                    <li>Pilih metode: Menggambar langsung atau Upload file gambar</li>
                    <li>Jika menggambar: Gunakan mouse/touchpad untuk menggambar</li>
                    <li>Jika upload: Pilih file gambar dari perangkat Anda (max 5MB)</li>
                    <li>Format yang diterima: JPG, PNG, GIF</li>
                </ul>
            </div>

            {/* Method Selection */}
            {!uploadedImage && (
                <div className="method-selection mb-6">
                    <div className="text-sm font-medium text-gray-700 mb-3">
                        Pilih Metode:
                    </div>
                    <div className="flex gap-4">
                        <button
                            onClick={() => setUploadMethod('canvas')}
                            className={`flex-1 p-4 rounded-lg border-2 font-semibold transition-all ${uploadMethod === 'canvas'
                                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                    : 'border-gray-300 bg-white text-gray-700 hover:border-indigo-300'
                                }`}
                            disabled={disabled}
                        >
                            🎨 Menggambar Langsung
                        </button>
                        <button
                            onClick={() => setUploadMethod('file')}
                            className={`flex-1 p-4 rounded-lg border-2 font-semibold transition-all ${uploadMethod === 'file'
                                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                    : 'border-gray-300 bg-white text-gray-700 hover:border-indigo-300'
                                }`}
                            disabled={disabled}
                        >
                            📁 Upload File
                        </button>
                    </div>
                </div>
            )}

            {/* Canvas Drawing Area */}
            {uploadMethod === 'canvas' && !uploadedImage && (
                <div className="canvas-area mb-4">
                    <canvas
                        ref={canvasRef}
                        width={600}
                        height={400}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        className="border-2 border-gray-400 rounded-lg cursor-crosshair bg-white w-full"
                        style={{ touchAction: 'none' }}
                    />
                    <div className="canvas-controls mt-3 flex gap-2">
                        <button
                            onClick={clearCanvas}
                            disabled={disabled || !hasDrawn}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            🗑️ Hapus Gambar
                        </button>
                    </div>
                </div>
            )}

            {/* File Upload Area */}
            {uploadMethod === 'file' && !uploadedImage && (
                <div className="upload-area mb-4">
                    <label className="block">
                        <div className="border-2 border-dashed border-gray-400 rounded-lg p-8 text-center cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-all">
                            <div className="text-4xl mb-3">📤</div>
                            <div className="text-lg font-semibold text-gray-700 mb-2">
                                Klik untuk upload gambar
                            </div>
                            <div className="text-sm text-gray-500">
                                atau drag & drop file di sini
                            </div>
                            <div className="text-xs text-gray-400 mt-2">
                                JPG, PNG, GIF (max 5MB)
                            </div>
                        </div>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileUpload}
                            disabled={disabled}
                            className="hidden"
                        />
                    </label>
                </div>
            )}

            {/* Uploaded Image Preview */}
            {uploadedImage && (
                <div className="image-preview mb-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                        Gambar Anda:
                    </div>
                    <div className="relative inline-block">
                        <img
                            src={uploadedImage}
                            alt="Uploaded drawing"
                            className="max-w-full h-auto rounded-lg shadow-lg border-2 border-green-400"
                        />
                        {!disabled && (
                            <button
                                onClick={removeImage}
                                className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-lg"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                    <div className="mt-3 text-sm text-green-600 font-semibold">
                        ✓ Gambar berhasil di-upload
                    </div>
                </div>
            )}

            {/* Validation Warning */}
            {!uploadedImage && (
                <div className="validation-warning mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                    <p className="text-sm text-yellow-800">
                        ⚠️ Anda harus menggambar atau upload gambar untuk melanjutkan
                    </p>
                </div>
            )}
        </div>
    );
};

export default QuestionDrawing;
